import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { getMinorUnitsSync, fromMinorUnits, convertMinorUnits } from '@sharecost/shared';
import { fetchExchangeRates } from '@/api/currencies';
import { distributeCrossCurrencyMinor } from '@/lib/splitDistribution';

export interface UserBalance {
  userId: string;
  balanceMinor: number;
  currency: string;
}

export interface SettlementSuggestion {
  fromUserId: string;
  toUserId: string;
  amountMinor: number;
  currency: string;
}

interface BalanceResult {
  balances: UserBalance[];
  suggestions: SettlementSuggestion[];
}

/**
 * Dual-tracking balance calculation.
 * Same-currency expenses use original minor units (no precision loss).
 * Cross-currency expenses convert via USD with proportional split distribution.
 */
async function calculateGroupBalances(
  groupId: string,
  groupCurrency: string
): Promise<BalanceResult> {
  const groupMinorUnits = getMinorUnitsSync(groupCurrency);

  // Fetch all data in parallel
  const [expenses, allSplits, settlements, rates] = await Promise.all([
    fetchAllRows(
      supabase
        .from('expenses')
        .select('id, paid_by, amount_minor_src, amount_minor_usd, currency_code_src')
        .eq('group_id', groupId)
        .eq('_deleted', false),
    ),
    fetchAllRows(
      supabase
        .from('expense_splits')
        .select('expense_id, user_id, amount_minor, amount_minor_usd')
        .eq('group_id', groupId)
        .eq('_deleted', false),
    ),
    fetchAllRows(
      supabase
        .from('settlements')
        .select('from_user_id, to_user_id, amount_minor, currency_code')
        .eq('group_id', groupId)
        .eq('_deleted', false),
    ),
    fetchExchangeRates(),
  ]);

  // Group splits by expense_id
  const splitsByExpense = new Map<string, typeof allSplits>();
  for (const s of allSplits) {
    const arr = splitsByExpense.get(s.expense_id) ?? [];
    arr.push(s);
    splitsByExpense.set(s.expense_id, arr);
  }

  // Balance accumulator (positive = owed money, negative = owes money)
  const balanceMap = new Map<string, number>();
  const addBalance = (userId: string, amount: number) => {
    balanceMap.set(userId, (balanceMap.get(userId) ?? 0) + amount);
  };

  // Process expenses
  for (const expense of expenses) {
    const splits = splitsByExpense.get(expense.id) ?? [];
    // Parity with mobile: an expense with no split rows is unattributable
    // (e.g. splits not yet synced). The mobile balance engine excludes such
    // expenses (validExpenseIds guard); counting them here would credit the
    // payer with nobody owing and produce different balances per platform.
    if (splits.length === 0) continue;
    const isSameCurrency = expense.currency_code_src === groupCurrency;

    if (isSameCurrency) {
      // Same currency — use original minor units directly
      addBalance(expense.paid_by, Number(expense.amount_minor_src));
      for (const split of splits) {
        addBalance(split.user_id, -Number(split.amount_minor));
      }
    } else {
      // Cross-currency — convert USD amounts to group currency
      const usdRate = rates.get('USD') ?? 1;
      const groupRate = rates.get(groupCurrency) ?? 1;
      // USD → group: multiply by (1 / groupRate) since groupRate is group→USD
      const usdToGroup = groupRate > 0 ? 1 / groupRate : 1;

      const expenseInGroup = convertMinorUnits(
        Number(expense.amount_minor_usd), usdToGroup, 2, groupMinorUnits
      );
      addBalance(expense.paid_by, expenseInGroup);

      // Distribute splits proportionally to each split's source-currency amount,
      // rounding half-up with the last split absorbing the remainder. Shared with
      // the mobile balance engine (see lib/splitDistribution.ts) so both platforms
      // produce identical per-member balances.
      const splitShares = distributeCrossCurrencyMinor(
        splits.map((s) => Number(s.amount_minor)),
        expenseInGroup
      );
      for (let i = 0; i < splits.length; i++) {
        addBalance(splits[i].user_id, -splitShares[i]);
      }
    }
  }

  // Process settlements
  for (const settlement of settlements) {
    const isSameCurrency = settlement.currency_code === groupCurrency;
    let amount: number;

    if (isSameCurrency) {
      amount = Number(settlement.amount_minor);
    } else {
      // Convert settlement currency to group currency via USD
      const settlementRate = rates.get(settlement.currency_code) ?? 1;
      const groupRate = rates.get(groupCurrency) ?? 1;
      const settlementMinorUnits = getMinorUnitsSync(settlement.currency_code);
      // settlement → USD → group
      const crossRate = groupRate > 0 ? settlementRate / groupRate : 1;
      amount = convertMinorUnits(
        Number(settlement.amount_minor), crossRate, settlementMinorUnits, groupMinorUnits
      );
    }

    addBalance(settlement.from_user_id, amount);  // Debtor paid, so balance goes up
    addBalance(settlement.to_user_id, -amount);    // Creditor received, balance goes down
  }

  const balances: UserBalance[] = Array.from(balanceMap.entries()).map(([userId, balanceMinor]) => ({
    userId,
    balanceMinor,
    currency: groupCurrency,
  }));

  // Generate settlement suggestions (greedy algorithm)
  const suggestions = calculateSettlementSuggestions(balances, groupCurrency);

  return { balances, suggestions };
}

/**
 * Greedy settlement optimization — minimizes number of transactions.
 */
function calculateSettlementSuggestions(
  balances: UserBalance[],
  currency: string
): SettlementSuggestion[] {
  const creditors: { userId: string; amount: number }[] = [];
  const debtors: { userId: string; amount: number }[] = [];

  for (const b of balances) {
    if (b.balanceMinor > 0) {
      creditors.push({ userId: b.userId, amount: b.balanceMinor });
    } else if (b.balanceMinor < 0) {
      debtors.push({ userId: b.userId, amount: -b.balanceMinor });
    }
  }

  // Sort descending
  creditors.sort((a, b) => b.amount - a.amount);
  debtors.sort((a, b) => b.amount - a.amount);

  const suggestions: SettlementSuggestion[] = [];
  let ci = 0;
  let di = 0;

  while (ci < creditors.length && di < debtors.length) {
    const transfer = Math.min(creditors[ci].amount, debtors[di].amount);
    if (transfer > 0) {
      suggestions.push({
        fromUserId: debtors[di].userId,
        toUserId: creditors[ci].userId,
        amountMinor: transfer,
        currency,
      });
    }

    creditors[ci].amount -= transfer;
    debtors[di].amount -= transfer;

    if (creditors[ci].amount === 0) ci++;
    if (debtors[di].amount === 0) di++;
  }

  return suggestions;
}

export function useGroupBalances(groupId: string | undefined, groupCurrency: string | undefined) {
  return useQuery({
    queryKey: ['balances', groupId],
    queryFn: () => calculateGroupBalances(groupId!, groupCurrency!),
    enabled: !!groupId && !!groupCurrency,
  });
}
